import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
} from '@backstage/core-plugin-api';
import type { AIActivity, FileCitation } from '../types/chat.types';
import type { PredefinedPromptProps } from '../types/predefinedPrompts';
import { processStream } from '../utils/streamProcessing';
import { createTokenBuffer, createBatchProcessor } from '../utils/streaming';
import type { TokenBuffer, BatchProcessor } from '../utils/streaming';

interface StreamingChatResult {
  sendMessage: (
    message: string,
    onChunk: (chunk: string) => void,
    onActivityChange?: (activity: AIActivity) => void,
    onCitation?: (citation: FileCitation) => void,
    previousResponseId?: string,
    predefinedPromptProps?: PredefinedPromptProps,
    onReasoningSummary?: (delta: string) => void,
    conversationId?: string,
    onConversationId?: (conversationId: string) => void,
  ) => Promise<string | null>;
  isStreaming: boolean;
  error: string | null;
  retry: () => Promise<void>;
  abort: () => void;
}

/**
 * Custom hook for streaming chat responses using SSE with performance optimizations.
 * Implements token buffering and batch processing to reduce React re-renders.
 */
export const useStreamingChat = (): StreamingChatResult => {
  const { fetch } = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs to maintain state across re-renders
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastMessageRef = useRef<string>('');
  const lastOnChunkRef = useRef<((chunk: string) => void) | null>(null);
  const lastOnActivityChangeRef = useRef<
    ((activity: AIActivity) => void) | null
  >(null);
  const lastOnCitationRef = useRef<((citation: FileCitation) => void) | null>(
    null,
  );
  const lastPredefinedPromptPropsRef = useRef<
    PredefinedPromptProps | undefined
  >(undefined);
  const lastConversationIdRef = useRef<string | undefined>(undefined);

  // Performance optimization refs
  const tokenBufferRef = useRef<TokenBuffer | null>(null);
  const batchProcessorRef = useRef<BatchProcessor | null>(null);

  /**
   * Handles flushing accumulated tokens from buffer to React state.
   * This is called by the batch processor at optimal intervals.
   */
  const handleBufferFlush = useCallback(() => {
    if (!tokenBufferRef.current || tokenBufferRef.current.isEmpty()) {
      return;
    }

    const { content, citations } = tokenBufferRef.current.flush();

    // Send accumulated content to the UI
    if (content && lastOnChunkRef.current) {
      lastOnChunkRef.current(content);
    }

    // Send accumulated citations to the UI
    if (citations.length > 0 && lastOnCitationRef.current) {
      citations.forEach(citation => {
        lastOnCitationRef.current?.(citation);
      });
    }
  }, []);

  /**
   * Cleans up streaming optimizations when session ends.
   */
  const cleanupStreamingOptimizations = useCallback(() => {
    if (batchProcessorRef.current) {
      batchProcessorRef.current.stop();
      batchProcessorRef.current = null;
    }
    if (tokenBufferRef.current) {
      tokenBufferRef.current.clear();
      tokenBufferRef.current = null;
    }
  }, []);

  /**
   * Initializes buffering and batch processing for a new streaming session.
   */
  const initializeStreamingOptimizations = useCallback(() => {
    // Clean up any existing buffers and processors
    cleanupStreamingOptimizations();

    // Create new token buffer for this streaming session
    tokenBufferRef.current = createTokenBuffer();

    // Create batch processor with error handling
    batchProcessorRef.current = createBatchProcessor({
      onFlush: handleBufferFlush,
      onError: batchError => {
        // eslint-disable-next-line no-console
        console.error('Batch processing error:', batchError);
        setError(`Batch processing failed: ${batchError.message}`);
      },
    });

    // Start the batch processing
    batchProcessorRef.current.start();
  }, [handleBufferFlush, cleanupStreamingOptimizations]);

  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // Clean up streaming optimizations
    cleanupStreamingOptimizations();
    setIsStreaming(false);
  }, [cleanupStreamingOptimizations]);

  const sendMessage = useCallback(
    async (
      message: string,
      onChunk: (chunk: string) => void,
      onActivityChange?: (activity: AIActivity) => void,
      onCitation?: (citation: FileCitation) => void,
      previousResponseId?: string,
      predefinedPromptProps?: PredefinedPromptProps,
      onReasoningSummary?: (delta: string) => void,
      conversationId?: string,
      onConversationId?: (conversationId: string) => void,
    ): Promise<string | null> => {
      // Cancel any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Store refs for retry functionality
      lastMessageRef.current = message;
      lastOnChunkRef.current = onChunk;
      lastOnActivityChangeRef.current = onActivityChange || null;
      lastOnCitationRef.current = onCitation || null;
      lastPredefinedPromptPropsRef.current = predefinedPromptProps;
      lastConversationIdRef.current = conversationId;

      // Initialize streaming optimizations
      initializeStreamingOptimizations();

      // Create new controller for this request
      abortControllerRef.current = new AbortController();

      setIsStreaming(true);
      setError(null);

      try {
        const baseUrl = await discoveryApi.getBaseUrl(
          'developer-portal-support-agent',
        );
        const url = `${baseUrl}/rag-service/portal-assistant`;

        // Make the streaming request
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            'Cache-Control': 'no-cache',
          },
          body: JSON.stringify({
            input: message,
            conversationId,
            previousResponseId,
            predefinedPromptProps,
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        // Create optimized callbacks that use buffering
        const optimizedOnChunk = (chunk: string) => {
          if (tokenBufferRef.current) {
            tokenBufferRef.current.addToken(chunk);
          } else {
            // Fallback to direct callback if buffer not available
            onChunk(chunk);
          }
        };

        const optimizedOnCitation = (citation: FileCitation) => {
          if (tokenBufferRef.current) {
            tokenBufferRef.current.addCitation(citation);
          } else {
            // Fallback to direct callback if buffer not available
            onCitation?.(citation);
          }
        };

        // Activity changes bypass buffering for immediate responsiveness
        const passthroughOnActivityChange = onActivityChange;

        // Reasoning summary bypasses buffering for immediate display
        const passthroughOnReasoningSummary = onReasoningSummary;

        // Process the streaming response with optimized callbacks
        return await processStream(
          response,
          optimizedOnChunk,
          passthroughOnActivityChange,
          optimizedOnCitation,
          passthroughOnReasoningSummary,
          onConversationId,
        );
      } catch (err) {
        if (err instanceof Error) {
          if (err.name === 'AbortError') {
            throw err;
          }
          setError(err.message);
        } else {
          setError('An unexpected error occurred');
        }
        throw err;
      } finally {
        // Final flush to ensure no data is lost
        if (batchProcessorRef.current) {
          batchProcessorRef.current.stop();
        }
        cleanupStreamingOptimizations();
        setIsStreaming(false);
        abortControllerRef.current = null;
      }
    },
    [
      discoveryApi,
      fetch,
      initializeStreamingOptimizations,
      cleanupStreamingOptimizations,
    ],
  );

  const retry = useCallback(async (): Promise<void> => {
    if (lastMessageRef.current && lastOnChunkRef.current) {
      await sendMessage(
        lastMessageRef.current,
        lastOnChunkRef.current,
        lastOnActivityChangeRef.current || undefined,
        lastOnCitationRef.current || undefined,
        undefined,
        lastPredefinedPromptPropsRef.current,
        undefined,
        lastConversationIdRef.current,
        undefined,
      );
    }
  }, [sendMessage]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      cleanupStreamingOptimizations();
    };
  }, [cleanupStreamingOptimizations]);

  return {
    sendMessage,
    isStreaming,
    error,
    retry,
    abort,
  };
};
